<template>
  <div class="k-toast k-glass-2" :style="{ '--k-toast-color': tone }" role="status" aria-live="polite">
    <span class="k-toast__badge">
      <v-icon :icon="icon" size="18" />
    </span>

    <span class="k-toast__text" v-html="text" />

    <v-btn
      v-if="actionText"
      class="k-toast__action"
      variant="text"
      size="small"
      @click="$emit('action')"
    >
      {{ actionText }}
    </v-btn>

    <v-btn
      class="k-toast__close"
      icon="mdi-close"
      variant="text"
      size="x-small"
      aria-label="Fermer"
      @click="$emit('close')"
    />

    <span
      v-if="timeout > 0"
      class="k-toast__bar"
      :style="{ animationDuration: `${timeout}ms` }"
      @animationend="$emit('close')"
    />
  </div>
</template>

<script>
const THEME_COLORS = ["primary", "accent", "secondary", "success", "info", "warning", "error"];

const ICONS = {
  success: "mdi-check",
  error: "mdi-alert-circle-outline",
  warning: "mdi-alert-outline",
  info: "mdi-information-outline",
};

export default {
  name: "KToast",
  props: {
    text: {
      type: String,
      default: "",
    },
    color: {
      type: String,
      default: "success",
    },
    timeout: {
      type: Number,
      default: 5000,
    },
    actionText: {
      type: String,
      default: "",
    },
  },
  emits: ["close", "action"],
  computed: {
    tone() {
      return THEME_COLORS.includes(this.color) ? `rgb(var(--v-theme-${this.color}))` : this.color;
    },
    icon() {
      return ICONS[this.color] || "mdi-information-outline";
    },
  },
};
</script>

<style scoped>
.k-toast {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  width: max-content;
  min-width: 280px;
  max-width: 100%;
  padding: 10px 8px 10px 10px;
  overflow: hidden;
  pointer-events: auto;
  background: var(--k-glass-3-bg);
  border-radius: var(--k-radius-lg);
}

.k-toast__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: var(--k-radius-md);
  color: var(--k-toast-color);
  background: color-mix(in srgb, var(--k-toast-color) 16%, transparent);
  animation: k-toast-pop var(--k-dur-slow) var(--k-ease-out) 120ms backwards;
}

.k-toast__text {
  flex: 1;
  min-width: 0;
  font-size: 13.5px;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.k-toast__action {
  flex: none;
  color: var(--k-toast-color);
}

.k-toast__close {
  flex: none;
  opacity: 0.55;
}

.k-toast__bar {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 2px;
  background: var(--k-toast-color);
  opacity: 0.7;
  transform-origin: left center;
  animation: k-toast-progress linear forwards;
}

.k-toast:hover .k-toast__bar {
  animation-play-state: paused;
}

@keyframes k-toast-progress {
  from {
    transform: scaleX(1);
  }

  to {
    transform: scaleX(0);
  }
}

@keyframes k-toast-pop {
  from {
    opacity: 0;
    transform: scale(0.5);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .k-toast__badge {
    animation: none;
  }
}
</style>
